import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import { SupportContactInline } from "@/components/SupportContact";
import {
  ForkKnife, Sparkle, Clock, CurrencyInr, CheckCircle, ArrowRight, Coffee, Lightning,
} from "@phosphor-icons/react";

const setSEO = (title, desc) => {
  document.title = title;
  const q = (sel) => document.querySelector(sel);
  q('meta[name="description"]')?.setAttribute("content", desc);
  q('meta[property="og:title"]')?.setAttribute("content", title);
  q('meta[property="og:description"]')?.setAttribute("content", desc);
};

const HIGHLIGHTS = [
  { icon: Sparkle,     title: "AI-designed in 30 seconds", desc: "Describe your restaurant, upload your logo — we generate 3 print-ready menu designs instantly." },
  { icon: Clock,       title: "Printed & delivered in 3 days", desc: "Once you approve, our print partners produce and ship — no back-and-forth." },
  { icon: CurrencyInr, title: "From ₹1,499 for 100 prints",    desc: "Includes design, printing, and delivery across India. Extra copies scale down fast." },
  { icon: ForkKnife,   title: "Made for Indian restaurants",   desc: "South Indian, North Indian, Chinese, café menus — the AI knows the format." },
];

const STEPS = [
  { n: "01", title: "Tell us about your restaurant", desc: "Name, cuisine, city, and paste your menu — even a WhatsApp text works." },
  { n: "02", title: "Get 3 AI menu concepts",         desc: "In 30 seconds. Pick a style, tweak with the AI editor if needed." },
  { n: "03", title: "Approve & pay",                  desc: "Secure Razorpay checkout. GST invoice generated automatically." },
  { n: "04", title: "Delivered in 3 days",            desc: "Print-ready PDFs + physical menu cards at your restaurant door." },
];

const FAQS = [
  { q: "What sizes can you print?", a: "Standard A4 single-side, A4 folded, A3 for wall menus, and pocket-size takeaway cards. Custom sizes on request." },
  { q: "Can I edit the menu later?", a: "Yes. Every order is stored — reorder with new prices or new dishes in one click. First price update is free." },
  { q: "Do you print in regional languages?", a: "Yes — Tamil, Telugu, Kannada, Hindi, Marathi, Bengali, Gujarati are supported. Just paste your menu in the language you want." },
  { q: "What paper stock do you use?", a: "300 GSM matte or gloss art card, laminated on request. Restaurants that need waterproof menus can opt for PVC / synthetic card at extra cost." },
  { q: "Is there a minimum order?", a: "Yes — 50 pieces minimum for print, but you can start with just the AI design (₹499 for 3 concepts, no print required)." },
];

export default function MenuDesign() {
  useEffect(() => {
    setSEO(
      "AI Menu Design for Restaurants — Talaio/AI",
      "AI-designed menu cards for Indian restaurants. Print-ready in 30 seconds, delivered in 3 days. From ₹1,499 for 100 prints. Design your menu online now."
    );
  }, []);

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />

      {/* Hero */}
      <section className="border-b border-[var(--pf-border)] bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
            <div className="pf-label mb-4 inline-flex items-center gap-2 text-[var(--pf-brand)]">
              <ForkKnife size={14} weight="fill"/> For Restaurants & Cafés
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              AI menu design.
              <br/><span className="text-[var(--pf-brand)]">Printed in 3 days.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-[var(--pf-text-2)] max-w-2xl">
              Stop paying a designer ₹5,000 and waiting two weeks. Talaio designs your restaurant
              menu with AI in 30 seconds, then prints and delivers 100 copies to your door for ₹1,499.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/brief/new?source=ai&product=menu&industry=Restaurant"
                data-testid="menu-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[var(--pf-brand)] hover:bg-[var(--pf-text)] text-white px-6 py-3.5 font-bold rounded-lg transition-colors">
                <Sparkle size={16} weight="fill"/> Design my menu now
              </Link>
              <Link to="/logo-studio"
                className="inline-flex items-center justify-center gap-2 border-2 border-[var(--pf-text)] hover:bg-[var(--pf-text)] hover:text-white text-[var(--pf-text)] px-6 py-3.5 font-semibold rounded-lg transition-colors">
                Need a logo first? →
              </Link>
            </div>
            <div className="mt-6">
              <SupportContactInline variant="form"/>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.5 }}
            className="lg:col-span-5">
            {/* Menu preview mockup — pure CSS, no image */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-[var(--pf-brand)]/10 rounded-2xl rotate-3"/>
              <div className="relative bg-white border border-[var(--pf-border)] rounded-2xl p-6 shadow-xl">
                <div className="text-center border-b border-[var(--pf-border)] pb-4 mb-4">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--pf-brand)]">Sample menu</div>
                  <div className="font-black text-2xl tracking-tighter mt-1" style={{ fontFamily: "serif" }}>Brew Lab</div>
                  <div className="text-xs text-[var(--pf-text-3)]">Speciality coffee · Chennai</div>
                </div>
                <div className="space-y-3">
                  {[
                    ["Filter Coffee",    "₹90"],
                    ["Cold Brew",        "₹180"],
                    ["Cardamom Latte",   "₹210"],
                    ["Chocolate Croissant", "₹150"],
                    ["Avocado Toast",    "₹280"],
                  ].map(([n, p]) => (
                    <div key={n} className="flex justify-between items-baseline text-sm">
                      <span className="font-semibold text-[var(--pf-text)]">{n}</span>
                      <span className="border-b border-dashed border-[var(--pf-text-3)] flex-1 mx-2 -mb-1"></span>
                      <span className="font-mono font-bold">{p}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-[var(--pf-border)] text-center">
                  <Coffee size={20} weight="duotone" className="text-[var(--pf-brand)] inline mb-1"/>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--pf-text-3)]">Freshly brewed. Freshly designed.</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Highlights */}
      <section className="border-b border-[var(--pf-border)] bg-[var(--pf-bg)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="pf-label mb-3">Why restaurants pick Talaio</div>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-10">Fast · Affordable · Made for India.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {HIGHLIGHTS.map(({ icon: Ic, title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="border border-[var(--pf-border)] bg-white p-6">
                <Ic size={30} weight="duotone" className="text-[var(--pf-brand)] mb-3"/>
                <h3 className="font-black text-lg tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-[var(--pf-text-2)] leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-b border-[var(--pf-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="pf-label mb-3">How it works</div>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-10">Four steps to printed menus.</h2>
          <div className="grid md:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="border-l-2 border-[var(--pf-brand)] pl-4">
                <div className="text-4xl font-black tracking-tighter text-[var(--pf-brand)]">{s.n}</div>
                <div className="font-black text-base tracking-tight mt-1">{s.title}</div>
                <p className="text-sm text-[var(--pf-text-2)] mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-b border-[var(--pf-border)] bg-[var(--pf-bg)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="pf-label mb-3">Pricing</div>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-3">Design + print + delivered.</h2>
          <p className="text-sm text-[var(--pf-text-3)] mb-10">All prices include GST · Delivered pan-India · Bulk discounts apply</p>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { name: "Starter",   qty: 50,  price: "₹999",   note: "Perfect for a soft launch" },
              { name: "Standard",  qty: 100, price: "₹1,499", note: "Most popular for cafés", highlight: true },
              { name: "Bulk",      qty: 500, price: "₹4,999", note: "Chains, multi-outlet restaurants" },
            ].map((t) => (
              <div key={t.name}
                className={`border-2 p-6 flex flex-col ${t.highlight ? "border-[var(--pf-brand)] bg-[var(--pf-brand)]/5 shadow-lg relative" : "border-[var(--pf-border)] bg-white"}`}>
                {t.highlight && (
                  <div className="absolute -top-3 left-6 bg-[var(--pf-brand)] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1">Most popular</div>
                )}
                <div className="pf-label mb-1">{t.name}</div>
                <div className="text-4xl font-black tracking-tighter">{t.price}</div>
                <div className="text-sm text-[var(--pf-text-2)] mt-1">{t.qty} menu cards · A4 · 300 GSM</div>
                <div className="text-xs text-[var(--pf-text-3)] mt-4 mb-6">{t.note}</div>
                <ul className="space-y-2 flex-1 text-sm">
                  <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-500"/> 3 AI-generated concepts</li>
                  <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-500"/> 3 free revisions with AI editor</li>
                  <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-500"/> Print + delivery included</li>
                  <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-500"/> GST invoice auto-generated</li>
                </ul>
                <Link to="/brief/new?source=ai&product=menu&industry=Restaurant"
                  className={`mt-6 py-3 px-5 font-bold rounded-lg transition-colors inline-flex items-center justify-center gap-2 ${t.highlight ? "bg-[var(--pf-brand)] hover:bg-[var(--pf-text)] text-white" : "border-2 border-[var(--pf-text)] hover:bg-[var(--pf-text)] hover:text-white text-[var(--pf-text)]"}`}>
                  Start now <ArrowRight size={14}/>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-xs text-[var(--pf-text-3)] mt-6 inline-flex items-center gap-1">
            <Lightning size={13} weight="fill" className="text-amber-500"/> First-order discount: mention <strong>MENU10</strong> at checkout for 10% off.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="pf-label mb-3">FAQ</div>
        <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-8">Common questions.</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details key={i} className="border border-[var(--pf-border)] bg-white p-5 group">
              <summary className="font-black text-base cursor-pointer flex justify-between items-center list-none">
                {f.q}
                <span className="text-[var(--pf-brand)] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-[var(--pf-text-2)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>

        <div className="mt-12 p-6 border border-[var(--pf-border)] bg-white text-center">
          <h3 className="text-2xl font-black tracking-tighter">Ready to redesign your menu?</h3>
          <p className="text-sm text-[var(--pf-text-2)] mt-2 mb-5">30 seconds. No signup needed.</p>
          <Link to="/brief/new?source=ai&product=menu&industry=Restaurant"
            data-testid="menu-cta-footer"
            className="inline-flex items-center gap-2 bg-[var(--pf-brand)] hover:bg-[var(--pf-text)] text-white px-6 py-3 font-bold rounded-lg transition-colors">
            <Sparkle size={16} weight="fill"/> Start free
          </Link>
          <div className="mt-6 pt-6 border-t border-[var(--pf-border)]">
            <SupportContactInline variant="form"/>
          </div>
        </div>
      </section>
    </div>
  );
}
